import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faArrowUpRightFromSquare, faEnvelope } from "@fortawesome/pro-regular-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";

import {
	CampaignChannel,
	CampaignDisplayStatus,
	CampaignSummary,
	TicketLinkedEntityType,
} from "@signalist/shared-types";

import { ReactComponent as ThreeDots } from "@assets/icons/three-dots.svg";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { MenuItem } from "@/components/ContextMenu/ContextMenu";
import { ReportProblemMenu } from "@/components/SupportTicket/ReportProblemMenu";
import { Icon } from "@/components/Icon/Icon";
import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { CampaignSubRow, ROW_ACTION_BUTTON } from "../../List/components/ListCampaignSubRows";

const isLoadingRow = (row: CampaignSubRow): row is { loading: true } => "loading" in row;

/** Sub-row selection checkbox for one campaign (the bulk bar acts on campaigns, not lists). */
export const CampaignSelectSubCell: FunctionComponent<{
	row: CampaignSubRow;
	isSelected: boolean;
	onToggle: (campaign: CampaignSummary) => void;
}> = ({ row, isSelected, onToggle }) => {
	if (isLoadingRow(row)) {
		return <span />;
	}

	return (
		<div className="flex items-center pl-1">
			<Checkbox isChecked={isSelected} name="selectedCampaign" onChange={() => onToggle(row)} />
		</div>
	);
};

const ChannelBadge: FunctionComponent<{ channel: CampaignChannel }> = ({ channel }) => {
	const { t } = useTranslation();

	return (
		<Tooltip content={t(`campaignsOverview.channels.${channel}`)}>
			<span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-text-meta">
				{channel === "email" ? (
					<Icon className="h-3 w-3" icon={faEnvelope} />
				) : (
					<BrandsIcon className="h-3 w-3" icon={faLinkedin} />
				)}
			</span>
		</Tooltip>
	);
};

/**
 * Campaign name sub-cell with the campaign's channel badge(s) in front (mockup parity) and an
 * on-hover "Open campaign" action that jumps into the campaign builder.
 */
export const CampaignNameChannelSubCell: FunctionComponent<{
	row: CampaignSubRow;
	onOpen: (campaign: CampaignSummary) => void;
}> = ({ row, onOpen }) => {
	const { t } = useTranslation();

	if (isLoadingRow(row)) {
		return <span className="pl-6 text-sm text-text-meta">{t("basics.wait")}</span>;
	}

	return (
		<div className="relative flex min-w-0 items-center gap-2 pl-6">
			{row.channels.map((channel) => (
				<ChannelBadge key={channel} channel={channel} />
			))}
			<span className="truncate font-medium text-text" title={row.name}>
				{row.name}
			</span>
			<div className="absolute right-0 top-1/2 hidden -translate-y-1/2 items-center gap-1.5 bg-gradient-to-r from-transparent to-background-light pl-6 group-hover/subrow:flex">
				<button
					className={ROW_ACTION_BUTTON}
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onOpen(row);
					}}
				>
					<Icon className="w-3 h-3" icon={faArrowUpRightFromSquare} />
					{t("campaignsOverview.rowMenu.openCampaign")}
				</button>
			</div>
		</div>
	);
};

/**
 * Campaigns-column cell of a list row: "N campaigns", green with a run indicator while at least
 * one campaign is active, grey otherwise (mockup parity — the List overview shows the bulk
 * toggle here instead; on this page run control lives on the campaign rows themselves).
 */
export const ListCampaignsCountPill: FunctionComponent<{
	summaries: CampaignSummary[] | undefined;
	campaignCount: number;
}> = ({ summaries, campaignCount }) => {
	const { t } = useTranslation();
	const runningCount = (summaries ?? []).filter(
		(campaign) => campaign.status === CampaignDisplayStatus.ACTIVE
	).length;
	const count = summaries?.length ?? campaignCount;

	return (
		<span
			className={classNames(
				"inline-flex max-w-[140px] items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold",
				runningCount > 0
					? "border-status-success bg-status-success-light text-status-success"
					: "border-transparent bg-gray-100 text-text-meta"
			)}
			title={
				runningCount > 0
					? t("campaignsOverview.countPill.running", { running: runningCount, count })
					: t("campaignsOverview.countPill.none")
			}
		>
			{runningCount > 0 && <span className="h-1.5 w-1.5 rounded-full bg-status-success" />}
			<span className="truncate">{t("campaignsOverview.countPill.label", { count })}</span>
		</span>
	);
};

/**
 * Row context menu of one campaign sub-row: Open / Pause–Resume / Duplicate (PREC-3880), plus the
 * flag-gated "Report a problem" (PREC-3937). A campaign has no numeric id of its own — it is a
 * uuid inside the list's campaignConfig — so the ticket links the **list** and names the campaign
 * in the prefilled subject and description.
 */
export const CampaignRowMenu: FunctionComponent<{
	row: CampaignSubRow;
	/** The parent list, which is what a ticket about this campaign can actually point at. */
	listId: number;
	onOpen: (campaign: CampaignSummary) => void;
	onToggleStatus: (campaign: CampaignSummary) => void;
	onDuplicate: (campaign: CampaignSummary) => void;
}> = ({ row, listId, onOpen, onToggleStatus, onDuplicate }) => {
	const { t } = useTranslation();

	if (isLoadingRow(row)) {
		return <span />;
	}

	const canToggle =
		row.status === CampaignDisplayStatus.ACTIVE || row.status === CampaignDisplayStatus.PAUSED;

	const options: MenuItem[] = [
		{ title: t("campaignsOverview.rowMenu.openCampaign"), onClick: () => onOpen(row) },
		...(canToggle
			? [
					{
						title:
							row.status === CampaignDisplayStatus.ACTIVE
								? t("campaignsOverview.rowMenu.pause")
								: t("campaignsOverview.rowMenu.resume"),
						onClick: () => onToggleStatus(row),
					},
				]
			: []),
		{ title: t("campaignsOverview.rowMenu.duplicate"), onClick: () => onDuplicate(row) },
	];

	return (
		<div className="flex justify-end gap-2">
			<ReportProblemMenu
				options={options}
				prefill={{
					linkedEntity: { type: TicketLinkedEntityType.LIST, id: listId },
					subject: t("support.modal.campaignSubject", { name: row.name }),
					message: t("support.modal.campaignBody", {
						name: row.name,
						campaignId: row.campaignId,
					}),
				}}
				width="170"
			>
				<SvgIcon className="w-[20px] h-[20px] text-gray-700 cursor-pointer" svgIcon={ThreeDots} />
			</ReportProblemMenu>
		</div>
	);
};
